import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { Grid, IconButton, Portal, Snackbar, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import classNames from 'classnames';
import * as Sentry from '@sentry/react';

import { INotify, NotifyTypes } from 'types/app';
import { DEFAULT_NOTIFY_OPTIONS } from 'constants/index';
import { USER_DISABLED } from 'features/auth/constants';
import Logout from 'startup/protected/Logout';

interface IProps extends INotify {
  show: boolean;
  type: NotifyTypes;
  onClose?: () => void;
}

const useStyles = makeStyles(theme => ({
  contentRoot: {
    top: theme.mixins.toolbar.minHeight,
  },
  info: {
    backgroundColor: theme.palette.common.white,
    borderLeft: `8px solid ${theme.palette.primary.main}`,
  },
  error: {
    backgroundColor: theme.palette.common.white,
    borderLeft: `8px solid ${theme.palette.error.main}`,
  },
  message: theme.mixins.breakWord,
  link: {
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
  },
}));

const Notify: FC<IProps> = props => {
  const { show, onClose, duration, type, i18n, message, component } = props;

  const [open, setOpen] = useState(false);
  const classes = useStyles();

  const isError = type === 'error' || type === 'queryError' || type === 'mutationError';
  const hasMessage = !!message || !!component || !!i18n || isError;
  const rootClasses = classNames(classes.contentRoot, {
    [classes.error]: isError,
    [classes.info]: type === 'info',
  });

  useEffect(() => {
    if (show && message !== USER_DISABLED) {
      setOpen(true);
    }
  }, [show]);

  if (message === USER_DISABLED) {
    return <Logout />;
  }

  function handleClose() {
    setOpen(false);
    onClose();
  }

  function getSnackbarProps() {
    if (isError) {
      return {};
    }

    return {
      autoHideDuration: duration,
      onClose: handleClose,
    };
  }

  function renderMessage(children: ReactNode | string) {
    return (
      <Grid container alignItems="center">
        <Grid item xs>
          <Typography variant="body2" className={classes.message}>
            {children}
          </Typography>
        </Grid>
        {isError && (
          <Grid item>
            <IconButton key="close" color="inherit" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
    );
  }

  function openDialog() {
    Sentry.showReportDialog();
  }

  function renderQueryError() {
    return (
      <Trans i18nKey="errors:query">
        Oh snap! We’ve encountered an error on this page. We’ve been notified, but if you like you
        can
        <Typography display="inline" variant="body2" onClick={openDialog} className={classes.link}>
          tell us what happened
        </Typography>
        .
      </Trans>
    );
  }

  function renderMutationError() {
    return (
      <Trans i18nKey="errors:mutation">
        Well this is awkward. Something went wrong during your request. We’ve been notified, but
        feel free to
        <Typography display="inline" variant="body2" onClick={openDialog} className={classes.link}>
          tell us what happened
        </Typography>
        .
      </Trans>
    );
  }

  function renderNotify() {
    if (type === 'queryError') {
      return renderMessage(renderQueryError());
    }

    if (type === 'mutationError') {
      return renderMessage(renderMutationError());
    }

    if (i18n) {
      return renderMessage(<Trans {...i18n} />);
    }

    if (message) {
      return renderMessage(String(message));
    }

    if (component) {
      return component;
    }

    return null;
  }

  return (
    <Portal>
      <Snackbar
        open={hasMessage && open}
        ContentProps={{ classes: { root: rootClasses } }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        message={renderNotify()}
        {...getSnackbarProps()}
      />
    </Portal>
  );
};

Notify.defaultProps = {
  ...DEFAULT_NOTIFY_OPTIONS,
  onClose: () => undefined,
};

export default Notify;
